import type { AgentAccent, KnowledgeBase, LintFinding, ListingHealth } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'

/**
 * `marketplace.maker` — every word the builder and the maker's own shelf say. English is the
 * source of truth and the type; anything that interpolates is a function returning a whole
 * sentence.
 *
 * Two reasons this is a catalogue rather than literals in JSX. The first is localization:
 * PRD-18 shipped en with pt-BR-ready structure (AGM-124), and PRD-27 is that second locale
 * arriving as a sibling file rather than as a rewrite. The second is the banned vocabulary
 * (AGM-022): "retrieval", "top-k", "token", "budget", "risk class", "model", "scope", "MCP",
 * "layer" and "bytes" may not appear in maker copy, and a lint that has one place to look can
 * actually enforce that. Administrator copy is exempt by design and lives in `admin.*` — an
 * administrator is entitled to the technical name of the thing they are running.
 *
 * PRD-26 adds one vocabulary for sharing (D-37), and it is load-bearing: "group", never
 * "team"; "Who can use it"; "Owned by", never "kept by"; "everyone who signs in", never "the
 * company"; "Send for review", "Waiting for review", "sharing request", "Withdraw request";
 * "Let it answer for everyone it reaches" and "Ask its managers", never the word for lending.
 * ADR-0018 ratifies each of those in Portuguese before a word of it was translated.
 *
 * Anything that interpolates is a function, never a template assembled at the call site, so a
 * translator sees whole sentences instead of fragments joined by `+`.
 *
 * PRD-27 adds one more shape to that rule: an entry that has to COUNT, JOIN or SIZE something
 * takes the reader's language as its first argument. Nothing here reaches for a locale of its
 * own — this module is pure, so the only locale it can see is the one a component handed it
 * from `useI18n()` — and nothing here spells a plural, a list separator or a thousands mark by
 * hand any more. Those three are `Intl`'s, and they are the three that are wrong in a second
 * language in ways nothing fails on.
 *
 * ── What is NOT here ──
 *
 * Publishing, the lifecycle and the two consent records (AGM-042) are `marketplace.publish`:
 * that copy is persisted verbatim as an audit record and carries a different review bar (D-7).
 * Form limits and every function that CONSUMES this catalogue live beside the components, in
 * `maker/maker-limits.ts`, `maker/publish-wording.ts`, `maker/knowledge-coverage.ts` and
 * `maker/sharing-request.ts` — a validation limit that can drift per locale is a bug (LOC-017).
 */

/**
 * What a sentence needs from the reader's language in order to count, join or size something.
 *
 * Written out structurally rather than as `Pick<I18n, …>`, and that is not a style choice: this
 * file is part of `Messages`, `I18n` holds a `Messages`, and naming `I18n` here would close a
 * type cycle through the aggregator. The shapes are exactly the provider's, so `useI18n()`
 * satisfies them by structure at every call site.
 *
 * As narrow as each entry actually needs (PRD-27 LOC-020). Capturing a locale at module scope
 * would freeze whichever one happened to be live when the bundle evaluated, silently, for the
 * rest of the session (F-3). Every caller already holds `useI18n()`.
 */
type Counting = { locale: Locale; formatCount: (value: number) => string }
type Listing = { formatList: (items: readonly string[]) => string }
type Language = { locale: Locale }

/**
 * A count with its noun, where the ENTRY owns both forms and this only chooses between them
 * (PRD-27 D-19).
 *
 * `Intl.PluralRules` rather than `count === 1`, and that is not a style preference: Brazilian
 * Portuguese puts ZERO in the `one` category — "0 documento", not "0 documentos" — so a
 * two-branch comparison is already wrong in one of the two shipped languages, on every empty
 * state, without failing anything. The noun never arrives from a call site either: the generic
 * `plural(count, singular, plural)` this catalogue used to export hid the noun from extraction
 * entirely, which is why it is gone rather than localized.
 */
const countOf = (fmt: Counting, count: number, forms: { one: string; other: string }): string =>
  `${fmt.formatCount(count)} ${pluralCategory(fmt.locale, count) === 'one' ? forms.one : forms.other}`

/** "1 person" · "8 people" — the count this catalogue reaches for most often. */
const people = (fmt: Counting, count: number): string => countOf(fmt, count, { one: 'person', other: 'people' })

/** "Legal (8 people)" — a group as a reach line names it. */
const sized = (fmt: Counting, group: { name: string; memberCount: number }): string =>
  `${group.name} (${people(fmt, group.memberCount)})`

/** Chooses between two whole sentences by plural category, for entries where the count changes
 * more of the sentence than its noun — a verb, or a participle that has to agree. */
const byCount = (fmt: Language, count: number, forms: { one: string; other: string }): string =>
  pluralCategory(fmt.locale, count) === 'one' ? forms.one : forms.other

export const maker = {
  people,
  sized,

  builder: {
    newTitle: 'New agent',
    newLead: 'Give it a name to start. Everything else can change until you publish it.',
    nameLabel: 'Name',
    namePlaceholder: 'Expense Buddy',
    /** Creating asks who it belongs to (PRD-26 D-19) — only when there is more than one answer. */
    ownerLead:
      'A group’s agent is maintained by all of the group’s managers, and counts against the group’s limit rather than yours.',
    create: 'Start building',
    creating: 'Creating…',
    createFailed: 'That agent could not be created.',
    quotaReached:
      'You already have as many agents as you are allowed. Retire one you no longer use, or ask an administrator to raise your limit.',
    notAllowed: 'You can create an agent for yourself, or for a group you manage.',
    nameRequired: 'Give the agent a name first.',
    backToList: 'Your agents',
    loading: 'Opening the builder…',
    loadFailed: 'This agent could not be opened.',
    /**
     * The refusal, told apart from the outage (DTL-013/014). `loadFailed` above is what a
     * broken API gets and it keeps its retry; this is what a boundary working as designed
     * gets, and its way out is the gallery — retrying a 404 forever was the dead end this
     * PRD opened on (F-4).
     */
    notAvailableTitle: 'This agent is not available',
    notAvailableBody: 'It may have been removed, or it may not be shared with you. Browse the agents you can use.',
    tryAgain: 'Try again',
    unsaved: 'Unsaved changes',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    revert: 'Undo changes',
    changeNoteLabel: 'What changed?',
    changeNotePlaceholder: 'Added the 2026 travel policy — optional, kept in the history',
    saveFailed: 'That change could not be saved.',
    readOnly: 'You can read this agent, but only the people who maintain it and its co-editors can change it.',
    /** D-53: an invited co-editor looks before deciding, and does nothing until they accept. */
    readOnlyInvited:
      'You were invited to help with this agent. Accept the invitation on Your agents to change it or talk to it.',
    conflictTitle: 'Someone saved this agent while you were editing',
    conflictBody:
      'Nothing you did was overwritten, and nothing of theirs was lost. Your version is still on screen — copy anything you want to keep, then reload to start from theirs.',
    conflictReload: 'Reload their version',
    conflictKeep: 'Keep editing mine'
  },

  /**
   * Who an agent belongs to, and moving it (PRD-26 D-19, D-32).
   *
   * The owner is accountable; a group's agent is maintained by that group's managers. A move
   * never changes who can use it — the sentence is on the dialog because it is the question
   * everyone moving something asks, and the answer is the reassuring one.
   */
  owner: {
    label: 'Owned by',
    move: 'Move to a group…',
    moveAnywhere: 'Move…',
    moveTitle: (agent: string): string => `Move ${agent}`,
    moveLead: 'Whoever owns an agent is accountable for it. A group’s agents are maintained by its managers.',
    moveRule: 'You can move it to a group you manage. An administrator can move it anywhere.',
    moveKeeps:
      'Moving never changes who can use it. The new owner’s limits apply: if it already holds as many agents as it may, the move is refused.',
    moveSubmit: 'Move',
    moving: 'Moving…',
    cancel: 'Cancel',
    moveFailed: 'It could not be moved.',
    /** Who maintains a group's agent, as a sentence names them. */
    managersOf: (group: string): string => `${group}’s managers`,
    noDestination: 'You do not manage another group to move it to. An administrator can move it anywhere.'
  },

  sections: {
    identity: {
      title: 'Identity',
      lead: 'How people recognize this agent in the gallery and in a conversation.',
      name: 'Name',
      namePlaceholder: 'Expense Buddy',
      nameHint: 'Two agents shared with the same people cannot share a name.',
      icon: 'Icon',
      iconPlaceholder: '💼',
      iconHint: 'One emoji, or leave it blank to use the first letters of the name.',
      accent: 'Colour',
      category: 'Category',
      categoryNone: '—',
      tagline: 'One-line summary',
      taglinePlaceholder: 'Answers expense questions from the current travel policy.',
      taglineHint: 'Shown on the card. Say what it does for someone, in one line.',
      description: 'Description',
      descriptionPlaceholder:
        'What this agent is for, who it is for, and what it will not do. Shown on its page, before anyone talks to it.',
      descriptionHint: 'People read this to decide whether to trust it. Say what it will not do, too.'
    },

    instructions: {
      title: 'Instructions',
      lead: 'Tell the agent how to behave, in plain sentences, the way you would brief a new colleague.',
      label: 'Instructions',
      placeholder:
        'You help employees with expense questions.\n\nAnswer from the travel policy only. Quote the rule you used. If someone asks about something the policy does not cover, say so and point them at finance@company.com.\n\nBe brief. Two or three sentences unless they ask for detail.',
      hint: 'Good instructions say who it helps, what to do when it does not know, and how to sound.',
      guidance: [
        'Say who it is for and what they usually need.',
        'Say what to do when the answer is not in its knowledge — asking someone is a good answer.',
        'Say how it should sound: brief, formal, step-by-step.',
        'Platform rules always win. Instructions cannot make it hide where an answer came from.'
      ],
      counted: (fmt: Counting, chars: number): string => countOf(fmt, chars, { one: 'character', other: 'characters' })
    },

    knowledge: {
      title: 'Knowledge',
      /*
       * Attaching is not sharing (PRD-26 D-16): each person gets answers from what they can use,
       * unless the base's managers let it answer through this agent for everyone it reaches.
       */
      lead: 'The knowledge bases it answers from. Attaching one shares nothing: each person gets answers only from what they can use, unless its managers let it answer for everyone this agent reaches.',
      loading: 'Looking for knowledge you can attach…',
      failed: 'The knowledge you can use could not be loaded. You can still save everything else.',
      /** D-46: the builder attaches and asks; the Knowledge page maintains. */
      manage: 'Knowledge page',
      manageHint: 'Create and maintain knowledge bases on the Knowledge page.',
      open: (base: string): string => `Open ${base} on the Knowledge page`,
      maintainTitle: 'You maintain',
      maintainEmpty: 'You do not maintain a knowledge base yet.',
      useTitle: 'You can use',
      useEmpty: 'Nothing else is shared with you yet.',
      emptyHint: 'Create one on the Knowledge page, or ask its owner to share theirs with a group you are in.',
      otherTitle: 'Attached, and not yours to use',
      otherLead:
        'It was attached before, or it answers through this agent. You can remove it here; you cannot attach it again unless it is shared with you.',
      unknownBase: 'A knowledge base you can no longer use',
      ownedBy: (owner: string): string => `Owned by ${owner}`,
      documents: (fmt: Counting, count: number): string => countOf(fmt, count, { one: 'document', other: 'documents' }),
      /** A base's holdings, in space rather than in files — the other half of what fills up. */
      baseSpace: (amount: string): string => `${amount} used`,
      /**
       * How much a base holds, for a maker who asked for none of the units of it.
       *
       * The maker plane may not speak in bytes (AGM-022), and the reason is not squeamishness:
       * the only decision this number supports is "am I near my limit", for which this is a
       * complete answer and "812,447" is a puzzle. `formatSpace` in `knowledge-coverage.ts`
       * words every larger tier through `Intl`; this is the one tier that is prose.
       */
      underOneMegabyte: 'under 1 MB',
      /** Rolled up from the files inside, so a maintainer can see at a glance what is usable. */
      state: {
        empty: 'Nothing in it yet',
        reading: 'Reading files…',
        ready: 'Ready',
        needs_attention: 'Something needs a look'
      } satisfies Record<KnowledgeBase['status'], string>,
      attachedCount: (fmt: Counting, count: number): string =>
        count === 0
          ? 'Nothing attached — it will answer from everything the reader can already use.'
          : `${countOf(fmt, count, { one: 'knowledge base', other: 'knowledge bases' })} attached.`,
      /** Coverage is measured against the audience being chosen in step 7, not the one last published. */
      checkedAgainst: 'Checked against who can use it (step 7).',
      unsaved: 'Save to see who gets answers from it.',
      coverageLoading: 'Checking who gets answers from these…',
      coverageFailed: 'Whether everyone gets answers from these could not be checked.',
      coverage: {
        covered: 'Everyone it reaches can use this.',
        lent: (by: string | null, byAdministrator = false): string =>
          by === null
            ? 'It answers from this for everyone it reaches — its managers allowed it.'
            : byAdministrator
              ? `It answers from this for everyone it reaches — ${by}, an administrator, allowed it.`
              : `It answers from this for everyone it reaches — ${by} allowed it.`,
        missingEveryone: (fmt: Counting, count: number): string =>
          `Not everyone who signs in can use this: ${people(fmt, count)} will get answers without it.`,
        missingGroups: (groups: string): string =>
          `People in ${groups} cannot use this, so they will get answers without it.`,
        missingPeople: (fmt: Counting, count: number): string =>
          `${people(fmt, count)} it reaches cannot use this, so they will get answers without it.`
      }
    },

    capabilities: {
      title: 'What it can do',
      lead: 'Beyond answering, this is everything the agent may reach for. It always asks before it acts.',
      empty: 'Nothing has been made available to makers yet.',
      emptyHint: 'An administrator publishes these, with an explanation of what each one does.',
      loading: 'Loading what this agent could do…',
      failed: 'That list could not be loaded. You can still save everything else.',
      reads: 'Reads information',
      acts: 'Can take actions — always asks first',
      otherSystem: 'On the platform',
      selectedCount: (fmt: Counting, count: number): string =>
        count === 0
          ? 'Nothing selected — it will answer and nothing else.'
          : `${countOf(fmt, count, { one: 'thing', other: 'things' })} selected.`
    },

    behavior: {
      title: 'Behaviour',
      lead: 'Two choices that change what people get back.',
      groundedLabel: 'Only answer from its knowledge',
      groundedOn:
        'It will say it does not know rather than guess when the answer is not in the documents you attached.',
      groundedOff: 'It answers from the documents first, and may add what it knows generally — saying which is which.',
      groundedWarning:
        'Attach at least one knowledge base before publishing, or this agent will have nothing to answer from.',
      memoryLabel: 'Remembers preferences',
      memoryOn: 'People can tell it how they like things done here, and it will remember for next time.',
      memoryOff: 'It starts fresh every conversation, apart from preferences people saved for themselves platform-wide.'
    },

    starters: {
      title: 'Conversation starters',
      lead: 'Up to four example questions. Tapping one fills the message box — it never sends on its own.',
      placeholder: 'What can I expense on a client dinner?',
      add: 'Add a starter',
      remove: (index: number): string => `Remove starter ${index + 1}`,
      full: 'Four is the maximum.'
    },

    audience: {
      title: 'Who can use it',
      /*
       * Exact rather than reassuring (PRD-20 G-13, PRD-26 D-13). Nothing is shared until
       * publication, and the rule for what waits is the product owner's: groups you manage go
       * live, other groups and everyone wait for an administrator.
       */
      lead: 'Nothing changes until you publish. Groups you manage go live then; other groups, and everyone, wait for an administrator’s review.',
      leadAdmin: 'Nothing changes until you publish. You are an administrator, so what you choose goes live then.',
      locked: 'Only the people who maintain it can choose who can use it.',
      /** The coverage summary under the picker (GRP-045): "Payroll Rules will not reach Legal (8 people)". */
      gap: (base: string, whom: string): string => `${base} will not reach ${whom}`,
      gapEveryone: (fmt: Counting, count: number): string => `${people(fmt, count)} who sign in`,
      gapPeople: (fmt: Counting, count: number): string => `${people(fmt, count)} it reaches`,
      seeKnowledge: 'see Knowledge'
    }
  },

  copyPolicy: {
    title: 'Copies',
    lead: 'Whether other people may start their own agent from this one.',
    auto: 'Match who can use it',
    autoHint: 'Allowed when everyone can use it, off when it is shared with specific groups.',
    allowed: 'Allowed',
    allowedHint: 'Anyone who can use it may make their own copy to change.',
    blocked: 'Not allowed',
    blockedHint: 'Nobody may copy it.'
  },

  lint: {
    title: 'Worth fixing before you publish',
    blockingTitle: 'Fix these before publishing',
    blocking: 'Blocks publishing',
    suggestion: 'Suggestion',
    field: (field: string): string => `in ${field}`,
    heading: {
      instructions_empty: 'The instructions are empty',
      instructions_short: 'The instructions are very short',
      contradicts_platform: 'This asks for something the platform will not do',
      impersonation: 'This reads as if it speaks for a group or department it does not',
      trust_mark: 'This claims a status only administrators can grant',
      reserved_name: 'That name is reserved',
      // Not prose advice like its neighbours: it reports on a restore, where an older version
      // held settings an administrator now manages for everyone.
      legacy_setting_removed: 'Some settings are now managed for you'
      // Keyed by the contract enum rather than by `string`: a `Record<string, string>` let a new
      // lint code compile and render as `legacy_setting_removed` on screen, which is what the
      // `?? finding.code` fallback downstream existed to paper over (PRD-27 D-9).
    } satisfies Record<LintFinding['code'], string>
  },

  health: {
    title: 'Needs your attention',
    ok: 'Everything is working',
    okBody: 'Its knowledge is reachable and everything it can do is available.',
    checkedAt: (when: string): string => `checked ${when}`,
    heading: {
      knowledge_missing: 'A knowledge base is missing',
      capability_disabled: 'Something it can do was turned off',
      capability_quarantined: 'Something it can do is paused',
      layer_dropped: 'Part of its setup could not be applied',
      coverage_gap: 'Some of the people it reaches miss out on its knowledge',
      owner_deactivated: 'This agent needs an owner'
      // As above: the server's own enum, so a new health code is a compile error here rather
      // than a raw identifier in a heading and a blank line where the remedy should be.
    } satisfies Record<ListingHealth['issues'][number]['code'], string>,
    remedy: {
      knowledge_missing: 'Remove it below, or ask its owner to restore it. Answers continue meanwhile.',
      capability_disabled:
        'Nothing you can change here — an administrator turned it off for everyone. Remove it if it is no longer needed.',
      capability_quarantined:
        'An administrator paused it while they check a change. It comes back on its own, or you can remove it.',
      layer_dropped: 'Save the agent again. If it comes back, an administrator needs to look at it.',
      coverage_gap:
        'Some people it reaches cannot use one of its knowledge bases, so they get answers without it. Knowledge, below, says who — and lets you ask its managers.',
      owner_deactivated:
        'Nobody maintains it now. An administrator can move it to a person or a group. It keeps answering meanwhile.'
    } satisfies Record<ListingHealth['issues'][number]['code'], string>
  },

  preview: {
    title: 'Preview',
    /**
     * "Draft preview" was true of every agent when a draft was the only thing you could
     * preview. It is not the state that makes this a preview — the preview always runs the
     * version on screen with its writes simulated, and on a live agent that version is the one
     * nobody else has yet. Naming the state was wrong twice over: it said "draft" on something
     * published, and it drew attention away from the part that matters (PRD-20 OWN-010).
     */
    caption: 'Preview — actions are simulated',
    captionHint: 'This runs the version on screen, with the same rules as the real thing. Nothing it does leaves here.',
    start: 'Start a preview',
    starting: 'Opening a preview…',
    startFailed: 'The preview could not be started.',
    retry: 'Try again',
    saveFirst: 'Save your changes to try them here.',
    placeholder: 'Ask it something…',
    send: 'Send',
    stop: 'Stop',
    thinking: 'thinking…',
    empty: 'Ask it a question the way someone else would.',
    simulated: 'Simulated — approving this here changes nothing outside the preview.',
    failedRun: 'That preview run did not finish.',
    quota: 'You have used up today’s previews. They reset tomorrow.'
  },

  versions: {
    title: 'History',
    lead: 'Every save is kept. You can go back to any of them.',
    open: 'History',
    close: 'Hide history',
    loading: 'Loading history…',
    empty: 'Nothing saved yet.',
    noNote: 'no note',
    restore: 'Go back to this',
    restoring: 'Restoring…',
    restoredTitle: 'Restored',
    restoredBody: (version: number): string => `You are now editing the agent as it was at save ${version}.`,
    droppedTitle: 'Some of it could not come back',
    droppedBody: 'These are no longer available to you, so they were left out:',
    lintTitle: 'This older version needs a look',
    failed: 'That version could not be restored.'
  },

  editors: {
    title: 'Co-editors',
    /**
     * Addressed to whoever reads it, and true for them (F-8). The people who maintain an agent —
     * its personal owner, or its owning group's managers — publish it, choose who can use it and
     * move it; co-editors change it; an administrator is accountable for nothing here but can do
     * all of it.
     */
    leadOwner: 'People who can change this agent with you. Only you can publish it, choose who can use it, or move it.',
    leadManager: (group: string): string =>
      `People who can change this agent with ${group}’s managers. Only its managers — you among them — can publish it, choose who can use it, or move it.`,
    leadOther: (owner: string): string =>
      `People who can change this agent. Only ${owner} can publish it, choose who can use it, or move it.`,
    leadOtherGroup: (group: string): string =>
      `People who can change this agent. Only ${group}’s managers can publish it, choose who can use it, or move it.`,
    leadAdmin: (owner: string): string => `People who can change this agent. ${owner} is accountable for it.`,
    invite: 'Invite',
    inviting: 'Inviting…',
    emailLabel: 'Their work email',
    emailPlaceholder: 'colleague@company.com',
    invited: 'Invited — waiting for them to accept',
    accepted: 'Editing with you',
    remove: (name: string): string => `Remove ${name}`,
    empty: 'Nobody else yet.',
    failed: 'That invitation could not be sent.',
    owner: 'Owner'
  },

  invites: {
    title: 'Invitations',
    lead: 'People who asked you to help with their agent. You can look first; you change it and talk to it once you accept.',
    fromPerson: (owner: string): string => `${owner} invited you`,
    fromGroup: (group: string): string => `Owned by ${group} — one of its managers invited you`,
    accept: 'Accept',
    decline: 'Decline',
    look: 'Look first',
    pending: 'Working…',
    failed: 'That could not be answered. Try again.'
  },

  mine: {
    title: 'Your agents',
    lead: 'Everything you own, maintain for a group, or help edit.',
    create: 'New agent',
    empty: 'You have not made an agent yet.',
    emptyBody: 'An agent is a name, a few sentences of instruction, and the documents it should answer from.',
    loading: 'Loading your agents…',
    failed: 'Your agents could not be loaded.',
    open: 'Open',
    coEditing: 'You co-edit this',
    attention: 'Needs attention',
    waitingForReview: 'Waiting for review',
    /** Who a live agent is for, on the shelf. Reads as a fact about reach, not as a setting. */
    audienceEveryone: 'everyone who signs in',
    audienceGroups: (fmt: Listing, groups: readonly string[]): string => fmt.formatList(groups) || 'nobody yet',
    updated: (when: string): string => `updated ${when}`
  },

  /**
   * The agent's colour, named.
   *
   * A pressed-button group rather than swatches alone: the colour's NAME is part of every
   * option, because a control whose only signal is hue is unusable to a reader who cannot
   * distinguish them and unlabelable to a screen reader (AGM-124). Which is also why these are
   * copy and not tokens — the palette itself lives in `marketplace/accents.ts`.
   */
  accents: {
    slate: 'Slate',
    teal: 'Teal',
    plum: 'Plum',
    moss: 'Moss',
    navy: 'Navy',
    clay: 'Clay'
  } satisfies Record<AgentAccent, string>,

  /**
   * The owner's insights panel (PRD-18 AGM-120/121).
   *
   * Maker vocabulary, which means the mechanism words are out (AGM-022) and so is anything
   * about money or usage accounting — an owner is accountable for whether their agent HELPS,
   * and the platform's costs are an administrator's business. Every number on this panel is
   * therefore either a count of people, a count of ratings, or a rate about answers.
   *
   * Two sentences here are promises about privacy and both are exactly true: the owner never
   * sees a conversation, and notes arrive without names and no sooner than a day later. Saying
   * either one loosely would misrepresent what a rater is agreeing to when they type.
   *
   * The counts below are interpolated exactly as they were rather than sent through
   * `formatCount` — grouping "1234" into "1,234" would move an English rendering, and this work
   * moves words, not renderings. What does follow the reader is the FORM chosen beside them:
   * `Intl.PluralRules`, which picks the other one at zero in Brazilian Portuguese, where zero is
   * singular (PRD-27 D-19).
   */
  insights: {
    open: 'Insights',
    close: 'Hide insights',
    title: 'How this agent is doing',
    lead: 'Counts, rates and what people chose to send you. You never see anyone’s conversations.',
    loading: 'Loading…',
    unavailable: 'Insights are not available yet',
    unavailableBody: 'Nothing has been recorded for this agent, or this deployment has not enabled the owner view.',

    conversations: 'Conversations',
    people: 'People who used it',
    helpful: 'Helpful',
    unhelpful: 'Not helpful',
    helpfulShare: (share: string): string => `${share} of ratings were helpful`,
    noRatings: 'Nobody has rated an answer yet.',

    zeroHitTitle: 'Questions it found nothing for',
    zeroHitHint: 'It searched what it is allowed to read and came back empty. Usually a missing document.',
    abstentionTitle: 'Questions it could not answer',
    abstentionHint: 'It said it did not know rather than guess. Some of these are the agent working correctly.',
    notMeasurable: 'Not enough use yet to say',

    versionsTitle: 'Ratings by version',
    versionsHint: 'Compare the version you just shared against the one before it.',
    versionLabel: (version: number): string => `Version ${version}`,
    versionUnknown: 'Before it was shared',
    ratingsLine: (helpful: number, unhelpful: number): string => `${helpful} helpful · ${unhelpful} not helpful`,
    versionsEmpty: 'No ratings yet.',

    notesTitle: 'What people wrote',
    notesHint: 'Without names, and never sooner than a day after they were written.',
    notesEmpty: 'No notes yet.',

    questionsTitle: 'Questions people sent you',
    questionsHint: 'Each one is a question someone chose to share because the answer did not cover it.',
    questionsEmpty: 'Nobody has sent you a question yet.',
    questionOpen: 'Waiting on you',
    questionResolved: 'Handled',

    healthTitle: 'Things that needed attention',
    healthEmpty: 'Nothing has gone wrong with this agent.',

    /**
     * Which attached material actually answers questions (AGM-120's knowledge half).
     *
     * The distinction every line here turns on is that **attaching is not using**. A base can be
     * attached, perfectly shared, full of documents, and never once be the source of an answer —
     * and until this block existed the only way its owner could find that out was to notice the
     * agent being vague. "Used" is stated as a count of answers rather than as a score, because a
     * count is what the platform records: an answer pointed at a passage from that base.
     *
     * No money, no file sizes as raw numbers, no mechanism words — the same vocabulary rules the
     * rest of the maker plane keeps (AGM-022).
     */
    knowledgeTitle: 'Which of your material gets used',
    knowledgeHint:
      'Attaching is not using. A knowledge base can be attached, shared and full, and still never be where an answer came from.',
    knowledgeEmpty: 'Nothing is attached to this agent, so its answers come from the model rather than your documents.',
    knowledgeSearches: 'Times it looked something up',
    knowledgeFoundNothing: 'Times it found nothing',
    knowledgeGrounded: 'Answers built on your documents',
    // "1 of 1 answers" was wrong in English before it was wrong in any other language.
    knowledgeGroundedHint: (fmt: Language, grounded: number, answers: number): string =>
      byCount(fmt, answers, {
        one: `${grounded} of ${answers} answer`,
        other: `${grounded} of ${answers} answers`
      }),
    knowledgeWindow: (fmt: Language, days: number): string =>
      byCount(fmt, days, { one: `Over the last ${days} day.`, other: `Over the last ${days} days.` }),
    baseHolding: (fmt: Language, documents: number, space: string): string =>
      `${byCount(fmt, documents, { one: `${documents} document`, other: `${documents} documents` })} · ${space}`,
    baseUsed: (fmt: Language, answers: number): string =>
      byCount(fmt, answers, { one: `answered ${answers} question`, other: `answered ${answers} questions` }),
    baseUnused: 'Not used yet',
    /**
     * The honest caveat on "Not used yet".
     *
     * What is counted is the last thirty days; what is listed is what is attached *now*. So a
     * knowledge base added yesterday reads as unused and has done nothing wrong, and an owner
     * about to remove one deserves to be told that before they do.
     */
    baseUnusedHint:
      'That is worth a look rather than a deletion — it may hold the answer to a question nobody has asked this month, or you may have only just added it.',
    baseLastUsed: (when: string): string => `last ${when}`,
    baseOwnedBy: (owner: string): string => `owned by ${owner}`
  }
}
